import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import EnhancedDialog from '../EnhancedDialog';
import { supabaseClient } from '../../utils/supabase';
import { handleOperationError } from '../../utils/supabase-operations';
import { queryPolicies } from '../../utils/queryPolicies';
import './RequestCloseDialog.css';

/** Reason recorded when the requester leaves the optional reason blank. */
export const DEFAULT_CLOSE_REQUEST_REASON = 'All offers are fake';

export interface RequestCloseRow {
  id?: unknown;
  merchant_label?: unknown;
}

/**
 * Small close-request popup opened from the Approvals board row action
 * (REG-654). Submits through the same submit_deal_close_request RPC as the
 * submission-panel form; the request lands in the Admin review queue and
 * nothing changes until an admin approves it.
 */
export function RequestCloseDialog({
  row,
  onClose,
}: {
  row: RequestCloseRow | null;
  onClose: () => void;
}): ReactElement | null {
  const submissionId = typeof row?.id === 'number' && Number.isFinite(row.id) ? row.id : 0;
  const merchantLabel = typeof row?.merchant_label === 'string' && row.merchant_label ? row.merchant_label : 'merchant';
  const [scope, setScope] = useState<'submission' | 'current_cycle'>('current_cycle');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const { data: dealCycle, isLoading: cycleLoading } = useQuery({
    queryKey: ['submission-deal-cycle', submissionId],
    queryFn: async () => {
      const { data, error } = await (supabaseClient.rpc as any)('get_submission_deal_cycle', {
        p_submission: submissionId,
      });
      if (error) throw error;
      return data as number | null;
    },
    enabled: submissionId > 0,
    ...queryPolicies.recordDetail,
    refetchOnReconnect: true,
  });

  // Whole-market close is the default, but it needs a deal cycle; fall back to
  // the single-submission scope when this submission has none.
  useEffect(() => {
    if (!cycleLoading && dealCycle == null) setScope('submission');
  }, [cycleLoading, dealCycle]);

  const submit = useCallback(async () => {
    setBusy(true);
    try {
      const { error } = await (supabaseClient.rpc as any)('submit_deal_close_request', {
        p_submission: submissionId,
        p_scope: scope,
        p_reason: reason.trim() || DEFAULT_CLOSE_REQUEST_REASON,
      });
      if (error) throw error;
      toast.success(scope === 'submission' ? 'Submission close requested' : 'Market close requested');
      onClose();
    } catch (error) {
      toast.error(handleOperationError(error, 'Failed to submit close request').userMessage);
    } finally {
      setBusy(false);
    }
  }, [onClose, reason, scope, submissionId]);

  if (!row || submissionId <= 0) return null;

  return (
    <EnhancedDialog
      isOpen
      onClose={busy ? () => {} : onClose}
      title={`Request close — ${merchantLabel}`}
      className="request-close-dialog"
      canOutsideClickClose={!busy}
      canEscapeKeyClose={!busy}
    >
      <div className="request-close-body">
        <label>
          Close
          <select value={scope} onChange={(event) => setScope(event.target.value as typeof scope)}>
            <option value="current_cycle" disabled={cycleLoading || dealCycle == null}>
              All Submissions
            </option>
            <option value="submission">This Submission</option>
          </select>
        </label>
        <label>
          Reason (optional)
          <textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            rows={2}
            maxLength={1000}
            placeholder={DEFAULT_CLOSE_REQUEST_REASON}
          />
        </label>
        <div className="request-close-buttons">
          <button type="button" className="sky-btn sky-btn-ghost" disabled={busy} onClick={onClose}>Cancel</button>
          <button type="button" className="sky-btn sky-btn-primary" disabled={busy} onClick={() => void submit()}>
            {busy ? 'Submitting…' : 'Submit to Admin'}
          </button>
        </div>
      </div>
    </EnhancedDialog>
  );
}

export default RequestCloseDialog;
